import { NextPage } from 'next';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import MetaTags from '../../../../components/layouts/MetaTags';
import MerchantCourseDetailsPageProtected from '../../../../molecules/merchant/resources/book/MerchantCourseDetailsPageProtected';
import MerchantResourceBookPage from '../../../../templates/merchants/resources/book/IndexTemplate';
import { BookingPageRepositoryForSSR } from '../../../../infrastructure/repositories/BookingPageRepository';
import { MerchantDetail } from '../../../../interfaces/merchants';
import { fetchMerchantDetail } from '../../../../infrastructure/api';
import { injectAppContextGetServerSideProps } from '../../../_app';
import ErrorTemplate from '../../../../templates/ErrorTemplate';
import { MerchantSite } from '../../../../components/MerchantSite';
import { usePushMerchantDataLayerEffect } from '../../../../hooks/gtm';
import { useRwgConversionTrackingToken } from 'src/hooks/rwg';
import { toThumbnailUrl } from 'src/utils/metatag';
import { MerchantBookingPageApiResponse } from 'src/interfaces/merchants/bookings';
import axios from 'axios';
import { sanitizeResourcePublicId } from 'src/utils/sanitizeResourcePublicId';
import { getServerSideApiClient } from 'src/openapi/axiosClient';
import { ReservationFlowWithThrottle } from 'src/components/ReservationFlowWithThrottle';
import { getDestinationFromV1ToV2 } from 'src/utils/redirectReservationFlowVersion';

interface Query {
  publicId: string;
  resource_id: string;
  selected_date?: string;
  selected_slot?: string;
  selected_lesson?: string;
  selected_course?: string;
  selected_staff?: string;
}

interface Props {
  session: string;
  merchantId: string;
  merchantDetail?: MerchantDetail;
  resourceId: string;
  selectedDate?: string;
  selectedSlot?: string;
  selectedLesson?: string;
  selectedCourse?: string;
  selectedStaff?: string;
  isUnpublishedSite: boolean;
  isAccepting: boolean;
  isPasswordProtected: boolean;
  token: string;
  requestId: string;
  isCoubicApp: boolean;
  bookingData?: MerchantBookingPageApiResponse;
  isRateLimited: boolean;
}

const MerchantResourceBookWithThrottle: NextPage<Props> = (props: Props) => (
  <ReservationFlowWithThrottle isRateLimited={props.isRateLimited}>
    <MerchantResourceBook {...props} />
  </ReservationFlowWithThrottle>
);

const MerchantResourceBook: NextPage<Props> = (props: Props) => {
  const {
    session,
    token,
    requestId,
    merchantId,
    merchantDetail,
    resourceId,
    selectedDate,
    selectedSlot,
    selectedLesson,
    selectedCourse,
    selectedStaff,
    isAccepting,
    isPasswordProtected,
    isUnpublishedSite,
    isCoubicApp,
    bookingData,
  } = props;
  const intl = useIntl();
  usePushMerchantDataLayerEffect({
    visit_merchant_id: merchantDetail?.id,
  });

  useRwgConversionTrackingToken();

  const [passwordProtected, setPasswordProtected] =
    useState(isPasswordProtected);
  const onPasswordSubmit = (isCorrectPassword: boolean) => {
    if (isCorrectPassword) {
      setPasswordProtected(false);
    }
  };

  return (
    <MerchantSite
      isUnPublished={isUnpublishedSite}
      isMerchantInfoExist={!!merchantDetail}
      isProtected={passwordProtected}
      protectedElement={
        <MerchantCourseDetailsPageProtected
          publicId={merchantId}
          servicePublicId={resourceId}
          session={session}
          token={token}
          requestId={requestId}
          onSubmit={onPasswordSubmit}
        />
      }
      logoImage={merchantDetail?.logo_image || null}
      merchantPublicId={merchantDetail?.public_id || null}
      displayName={merchantDetail?.display_name || null}
      isCoubicApp={isCoubicApp}
    >
      {merchantDetail ? (
        <>
          <MetaTags
            title={intl.formatMessage({ id: 'books.meta.title' })}
            noIndex={true}
            googleAnalyticsFourTrackingId={
              merchantDetail.available_google_analytics_four_tracking_id
            }
            thumbnailUrl={
              bookingData
                ? toThumbnailUrl({
                    merchantDetail,
                    bookingData: bookingData,
                  })
                : undefined
            }
            faviconImageUrl={merchantDetail?.favicon_image?.url}
          />
          <MerchantResourceBookPage
            merchantId={merchantId}
            merchantDetail={merchantDetail}
            resourceId={resourceId}
            selectedDate={selectedDate}
            selectedSlot={selectedSlot}
            selectedLesson={selectedLesson}
            selectedCourse={selectedCourse}
            selectedStaff={selectedStaff}
            isAccepting={isAccepting}
            isCoubicApp={isCoubicApp}
          />
        </>
      ) : (
        <ErrorTemplate statusCode={404} />
      )}
    </MerchantSite>
  );
};
export const getServerSideProps = injectAppContextGetServerSideProps<Props>(
  async (context) => {
    const { query, session, token, requestId, isCoubicApp } = context;
    const {
      publicId: merchantPublicId,
      resource_id,
      selected_date,
      selected_slot,
      selected_lesson,
      selected_course,
      selected_staff,
    } = query as unknown as Query;

    const defaultProps: Props = {
      session,
      token,
      requestId,
      isCoubicApp,
      merchantId: merchantPublicId,
      resourceId: resource_id,
      selectedDate: selected_date,
      selectedSlot: selected_slot,
      selectedLesson: selected_lesson,
      selectedCourse: selected_course,
      selectedStaff: selected_staff,
      isUnpublishedSite: false,
      isAccepting: false,
      isPasswordProtected: false,
      isRateLimited: false,
    };

    const client = getServerSideApiClient(context);
    let isRateLimited = false;
    try {
      const { csrfToken } = await client.getCsrfToken();
      await client.postReservationFlowMerchantThrottle({
        parameter: {
          merchant_public_id: merchantPublicId,
          'X-Csrf-Token': csrfToken,
        },
      });
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 429) {
        isRateLimited = true;
      } else {
        throw e;
      }
    }

    if (isRateLimited) {
      context.res.statusCode = 429;
      return {
        props: {
          ...defaultProps,
          isRateLimited,
        },
      };
    }

    const merchantDetailResponse = await fetchMerchantDetail({
      session,
      token,
      requestId,
      merchantPublicId,
    });
    if (merchantDetailResponse.redirect) {
      return { redirect: merchantDetailResponse.redirect };
    }
    const { merchant, isUnpublishedSite } = merchantDetailResponse;

    if (isUnpublishedSite) {
      return {
        props: {
          ...defaultProps,
          merchantDetail: merchant,
          isUnpublishedSite,
        },
      };
    }

    const sanitizedResourcePublicId = sanitizeResourcePublicId(resource_id);
    const repository = BookingPageRepositoryForSSR(session, token, requestId);
    const { bookable } = await repository.Bookable(
      merchantPublicId,
      resource_id,
    );
    const isAccepting = !!bookable?.accepting;
    const isPasswordProtected = !!bookable?.protected;

    // axios.getだとcookieが載らないので、BookingPageRepositoryForSSR経由で取得
    let bookingData: MerchantBookingPageApiResponse | undefined;

    try {
      bookingData = await repository.Find(
        merchantPublicId,
        sanitizedResourcePublicId,
      );
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 401) {
        // パスワードプロテクトされたページを未ログイン状態で見た時は401なのでそれを無視する
      } else {
        throw e;
      }
    }

    const flowVersionResult = await client.getReservationFlowVersion({
      parameter: {
        merchant_public_id: merchantPublicId,
        resource_public_id: sanitizedResourcePublicId,
      },
    });

    const shouldBeRedirectedToV2 =
      flowVersionResult.reservation_flow_version === 'v2';

    // 新予約フローが有効な場合、新予約フローにリダイレクトする
    // bookingData が必要なのでここで判定する
    if (shouldBeRedirectedToV2) {
      return {
        redirect: {
          destination: getDestinationFromV1ToV2(
            {
              resource_public_id: sanitizedResourcePublicId,
              query,
              bookingData,
            },
            'next',
          ),
          permanent: false,
        },
      };
    }

    const props: Props = {
      ...defaultProps,
      merchantDetail: merchant,
      bookingData,
      isAccepting,
      isPasswordProtected,
    };

    return { props };
  },
);

export default MerchantResourceBookWithThrottle;
